import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ShareRecipeModal from "@/components/ShareRecipeModal";
import LogKcalButton from "@/components/LogKcalButton";

const FALLBACK_IMG = "https://images.pexels.com/photos/842545/pexels-photo-842545.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const MEAL_TYPES = [
  { key: "all", label: "All", icon: "🍽️" },
  { key: "breakfast", label: "Breakfast", icon: "🌅" },
  { key: "lunch", label: "Lunch", icon: "🌞" },
  { key: "dinner", label: "Dinner", icon: "🌙" },
  { key: "snack", label: "Snacks", icon: "🍿" },
  { key: "drink", label: "Drinks", icon: "🥤" },
];
const MEAL_LABEL = {
  breakfast: { icon: "🌅", label: "Breakfast" },
  lunch:     { icon: "🌞", label: "Lunch" },
  dinner:    { icon: "🌙", label: "Dinner" },
  snack:     { icon: "🍿", label: "Snack" },
  drink:     { icon: "🥤", label: "Drink" },
  other:     { icon: "🍽️", label: "Other" },
};

export default function Recipes() {
  const { user } = useAuth();
  const isPro = !!user?.is_pro;
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [parsing, setParsing] = useState(false);
  const [scanningPhoto, setScanningPhoto] = useState(false);
  const [pastedText, setPastedText] = useState("");
  const [textParsing, setTextParsing] = useState(false);
  const [textPanelOpen, setTextPanelOpen] = useState(false);
  const photoInputRef = useRef(null);
  const [selected, setSelected] = useState([]);
  const [servings, setServings] = useState({}); // recipe_id -> int
  const [scalingOpen, setScalingOpen] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [staplesBusy, setStaplesBusy] = useState(null);
  const [staplesDone, setStaplesDone] = useState(() => new Set());
  const [activeType, setActiveType] = useState("all");
  const [query, setQuery] = useState("");
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/recipes");
      setRecipes(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Deep-link from the Food Scanner QR flow: `?import=<url>` auto-runs the URL
  // parser so the user doesn't need to re-paste the link.
  useEffect(() => {
    const importUrl = searchParams.get("import");
    if (!importUrl) return;
    (async () => {
      setParsing(true);
      try {
        const { data } = await api.post("/recipes/parse", { url: importUrl }, { timeout: 60000 });
        toast.success(`Saved ${data.title}`);
        await load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't import that recipe — try pasting the link manually.");
      } finally {
        setParsing(false);
        setSearchParams({}, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parse = async (e) => {
    e.preventDefault();
    if (!url) return;
    setParsing(true);
    try {
      const { data } = await api.post("/recipes/parse", { url });
      toast.success(`Saved ${data.title}`);
      setUrl("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't parse that URL");
    } finally {
      setParsing(false);
    }
  };

  const scanPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset the input so the same file can be re-picked later
    e.target.value = "";
    setScanningPhoto(true);
    try {
      const b64 = await compressImageToBase64(file, { maxSide: 1600, quality: 0.82 });
      const { data } = await api.post("/recipes/parse-photo", { image_base64: b64 }, { timeout: 45000 });
      toast.success(`Saved ${data.title}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read that photo — try a clearer, well-lit shot.");
    } finally {
      setScanningPhoto(false);
    }
  };

  // Resize + JPEG-compress on the client so cookbook photos are ~200-400 KB
  // instead of the raw 5-8 MB phone camera output. Keeps the AI OCR sharp
  // enough and keeps the recipe doc tiny when we store the hero as a data URL.
  const compressImageToBase64 = (file, { maxSide = 1600, quality = 0.82 } = {}) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const parseText = async () => {
    const trimmed = pastedText.trim();
    if (trimmed.length < 10) {
      toast.error("Paste more text — at least a title and a few ingredients.");
      return;
    }
    setTextParsing(true);
    try {
      // Smart routing: if the paste is just a URL (no other text), send it to the
      // URL parser instead of the text parser — otherwise we'd tell the user
      // "no ingredients in this URL string" which isn't the fix they want.
      const looksLikeUrlOnly = /^https?:\/\/\S+$/i.test(trimmed);
      if (looksLikeUrlOnly) {
        const { data } = await api.post("/recipes/parse", { url: trimmed }, { timeout: 45000 });
        toast.success(`Saved ${data.title}`);
      } else {
        const { data } = await api.post("/recipes/parse-text", { text: trimmed }, { timeout: 45000 });
        toast.success(`Saved ${data.title}`);
      }
      setPastedText("");
      setTextPanelOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't extract a recipe — include ingredients and/or method.");
    } finally {
      setTextParsing(false);
    }
  };

  const addStaples = async (recipe) => {
    setStaplesBusy(recipe.recipe_id);
    try {
      const { data } = await api.post(`/recipes/${recipe.recipe_id}/add-missing-staples`, {});
      const n = data.added?.length || 0;
      const already = data.already_in_list?.length || 0;
      if (n && already) toast.success(`Added ${n} staple${n === 1 ? "" : "s"} — ${already} already in your list.`);
      else if (n) toast.success(`Added ${n} staple${n === 1 ? "" : "s"} to your shopping list.`);
      else toast.info("All staples were already in your shopping list.");
      setStaplesDone((s) => new Set(s).add(recipe.recipe_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add staples");
    } finally {
      setStaplesBusy(null);
    }
  };

  const toggleSelect = (r) => {    setSelected((s) => {
      const has = s.includes(r.recipe_id);
      if (has) return s.filter(x => x !== r.recipe_id);
      setServings((sv) => ({ ...sv, [r.recipe_id]: sv[r.recipe_id] || r.servings || 2 }));
      return [...s, r.recipe_id];
    });
  };

  const remove = async (id) => {
    if (!confirm("Delete this recipe?")) return;
    try {
      await api.delete(`/recipes/${id}`);
      setRecipes((r) => r.filter(x => x.recipe_id !== id));
      setSelected((s) => s.filter(x => x !== id));
      toast.success("Removed");
    } catch {
      toast.error("Could not delete");
    }
  };

  const buildList = async () => {
    if (selected.length === 0) {
      toast.error("Pick at least one recipe");
      return;
    }
    setCreatingList(true);
    try {
      const payload = {
        recipe_ids: selected,
        servings: selected.map((id) => ({ recipe_id: id, servings: Number(servings[id]) || 2 })),
      };
      const { data } = await api.post("/shopping-lists", payload);
      toast.success("Shopping list ready");
      nav(`/app/shopping/${data.list_id}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        toast.error(err?.response?.data?.detail || "Free plan limit reached", {
          duration: 7000,
          action: { label: "Upgrade", onClick: () => nav("/app/billing") },
        });
      } else {
        toast.error(err?.response?.data?.detail || "Could not create list");
      }
    } finally {
      setCreatingList(false);
      setScalingOpen(false);
    }
  };

  const selectedRecipes = recipes.filter(r => selected.includes(r.recipe_id));

  // One-tap: send a single recipe directly to a fresh shopping list.
  // Uses the recipe's default servings — no scaling modal.
  const sendOneToList = async (r) => {
    setSendingId(r.recipe_id);
    try {
      const payload = {
        recipe_ids: [r.recipe_id],
        servings: [{ recipe_id: r.recipe_id, servings: Number(servings[r.recipe_id]) || r.servings || 2 }],
      };
      const { data } = await api.post("/shopping-lists", payload);
      toast.success(`${r.title} added — opening your list`);
      nav(`/app/shopping/${data.list_id}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        toast.error(err?.response?.data?.detail || "Free plan limit reached", {
          duration: 7000,
          action: { label: "Upgrade", onClick: () => nav("/app/billing") },
        });
      } else {
        toast.error(err?.response?.data?.detail || "Could not send to list");
      }
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div data-testid="recipes-page" className="space-y-10 ff-fade-in">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ff-label">Your kitchen</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Recipes you love.
          </h1>
          <p className="text-[#5C6B61] mt-2 max-w-xl">
            Paste any recipe link — we&apos;ll parse the ingredients, count the calories, and queue them for the shop.
          </p>
        </div>
        {selected.length > 0 && (
          <button
            onClick={() => setScalingOpen(true)}
            data-testid="add-to-shopping-list-btn"
            className="ff-btn-primary self-start"
          >
            Build shopping list ({selected.length})
          </button>
        )}
      </header>

      {/* Parse URL */}
      <form onSubmit={parse} className="ff-card p-5 md:p-6 flex flex-col md:flex-row gap-3">
        <input
          data-testid="paste-url-input"
          className="ff-input flex-1"
          placeholder="Paste a recipe URL — e.g. https://www.bbcgoodfood.com/recipes/..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <button
          type="submit"
          disabled={parsing}
          data-testid="parse-recipe-btn"
          className="ff-btn-primary disabled:opacity-60 whitespace-nowrap"
        >
          {parsing ? "Reading…" : "Save recipe"}
        </button>
      </form>

      {/* Scan a recipe from a photo (cookbook page, menu, handwritten card) */}
      <button
        type="button"
        onClick={() => photoInputRef.current?.click()}
        disabled={scanningPhoto}
        data-testid="scan-recipe-photo-btn"
        className="w-full ff-card p-4 md:p-5 flex items-center gap-4 text-left hover:shadow-md transition disabled:opacity-60"
        style={{ background: "linear-gradient(135deg,#E8F0E9 0%,#F3F0EA 100%)", border: "1px solid #C7CBB7" }}
      >
        <span className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-2xl shrink-0">📸</span>
        <div className="min-w-0 flex-1">
          <div className="font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
            {scanningPhoto ? "Reading the photo…" : "Scan a cookbook page or menu"}
          </div>
          <div className="text-xs text-[#5C6B61] mt-0.5">
            Snap any recipe photo — cookbook page, printed menu, handwritten card — we&apos;ll extract the full list, steps and macros.
          </div>
        </div>
        <span className="text-[#3B5A3E] text-sm font-medium hidden sm:block">
          {scanningPhoto ? "…" : "Snap →"}
        </span>
      </button>
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={scanPhoto}
        data-testid="scan-recipe-photo-input"
      />

      {/* Paste-text fallback (Facebook, Instagram, TikTok captions, WhatsApp shares, etc.) */}
      <div className="ff-card overflow-hidden" data-testid="paste-text-card">
        <button
          type="button"
          onClick={() => setTextPanelOpen((v) => !v)}
          data-testid="paste-text-toggle"
          className="w-full p-4 md:p-5 flex items-center gap-4 text-left"
        >
          <span className="w-12 h-12 rounded-full bg-[#F3F0EA] flex items-center justify-center text-2xl shrink-0">📝</span>
          <div className="min-w-0 flex-1">
            <div className="font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
              Paste a caption (Facebook, Instagram, TikTok, WhatsApp)
            </div>
            <div className="text-xs text-[#5C6B61] mt-0.5">
              For social posts where the URL doesn&apos;t open — copy the caption text, paste here, we&apos;ll extract it.
            </div>
          </div>
          <span className="text-[#3B5A3E] text-sm font-medium">{textPanelOpen ? "Hide" : "Open"}</span>
        </button>
        {textPanelOpen && (
          <div className="px-4 pb-4 md:px-5 md:pb-5 space-y-3">
            <textarea
              rows={6}
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              placeholder="Paste the recipe caption / description here. (If you only have a link, paste it and we&apos;ll open it for you.)"
              className="ff-input w-full resize-y"
              data-testid="paste-text-input"
              maxLength={8000}
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] text-[#8B9690]">{pastedText.length} / 8000 chars</span>
              <button
                type="button"
                onClick={parseText}
                disabled={textParsing || pastedText.trim().length < 10}
                data-testid="paste-text-submit"
                className="ff-btn-primary disabled:opacity-60"
              >
                {textParsing ? "Reading…" : "Extract recipe"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Recipes grid */}
      {loading ? (
        <div className="text-[#5C6B61]">Loading…</div>
      ) : recipes.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Meal-type filter + search */}
          <div className="flex flex-col gap-3" data-testid="recipes-filters">
            <div className="flex flex-wrap gap-2">
              {MEAL_TYPES.map((mt) => {
                const count = mt.key === "all"
                  ? recipes.length
                  : recipes.filter((r) => (r.meal_type || "other") === mt.key).length;
                if (mt.key !== "all" && count === 0) return null;
                const active = activeType === mt.key;
                return (
                  <button
                    key={mt.key}
                    onClick={() => setActiveType(mt.key)}
                    data-testid={`filter-type-${mt.key}`}
                    className={`text-xs font-medium rounded-full px-3 py-1.5 transition flex items-center gap-1.5 ${
                      active
                        ? "bg-[#3B5A3E] text-white"
                        : "bg-[#F3F0EA] text-[#3B5A3E] hover:bg-[#E5E2DA]"
                    }`}
                  >
                    <span aria-hidden>{mt.icon}</span>
                    <span>{mt.label}</span>
                    <span className={`text-[10px] ${active ? "opacity-80" : "text-[#8B9690]"}`}>· {count}</span>
                  </button>
                );
              })}
            </div>
            <input
              type="search"
              placeholder="Search your recipes…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="recipes-search-input"
              className="ff-input max-w-md"
            />
          </div>

          {(() => {
            const q = query.trim().toLowerCase();
            const filtered = recipes.filter((r) => {
              if (activeType !== "all" && (r.meal_type || "other") !== activeType) return false;
              if (q) {
                const hay = `${r.title || ""} ${(r.tags || []).join(" ")}`.toLowerCase();
                if (!hay.includes(q)) return false;
              }
              return true;
            });
            if (filtered.length === 0) {
              return (
                <div className="ff-card p-8 text-center text-[#5C6B61]" data-testid="recipes-empty-filter">
                  <p>No recipes match those filters.</p>
                  <button
                    onClick={() => { setActiveType("all"); setQuery(""); }}
                    className="mt-3 text-sm text-[#3B5A3E] underline-offset-4 hover:underline"
                  >
                    Clear filters
                  </button>
                </div>
              );
            }
            return (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((r) => {
            const isSel = selected.includes(r.recipe_id);
            const mt = MEAL_LABEL[r.meal_type] || MEAL_LABEL.other;
            return (
              <div
                key={r.recipe_id}
                data-testid="recipe-card"
                className={`ff-card ff-card-hover overflow-hidden flex flex-col ${isSel ? "ring-2 ring-[#3B5A3E]" : ""}`}
              >
                <Link to={`/app/recipes/${r.recipe_id}`} className="block">
                  <div className="aspect-[16/10] bg-[#F3F0EA] overflow-hidden relative">
                    <img
                      src={r.image || FALLBACK_IMG}
                      alt={r.title}
                      onError={(e) => { e.target.src = FALLBACK_IMG; }}
                      className="w-full h-full object-cover"
                    />
                    {r.meal_type && r.meal_type !== "other" && (
                      <span
                        className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/95 backdrop-blur text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full shadow"
                        style={{ color: "#3B5A3E" }}
                        data-testid={`meal-type-pill-${r.recipe_id}`}
                      >
                        <span aria-hidden>{mt.icon}</span> {mt.label}
                      </span>
                    )}
                  </div>
                </Link>
                <div className="p-5 flex flex-col gap-3 flex-1">
                  <Link to={`/app/recipes/${r.recipe_id}`}>
                    <h3 className="text-lg font-medium leading-snug" style={{ fontFamily: "Outfit, sans-serif" }}>{r.title}</h3>
                  </Link>
                  <div className="flex flex-wrap gap-2">
                    {r.calories_per_serving != null && (
                      <span data-testid="recipe-calories" className="ff-chip" style={{ background: "#FBEFEA", color: "#A65541" }}>
                        🔥 {r.calories_per_serving} kcal / serving
                      </span>
                    )}
                    {(r.protein_g != null || r.carbs_g != null || r.fat_g != null) && (
                      <span
                        data-testid={`recipe-macros-${r.recipe_id}`}
                        className="ff-chip"
                        style={{ background: "#EDF3E9", color: "#3B5A3E" }}
                        title="Macros per serving"
                      >
                        P {Math.round(r.protein_g || 0)}g · C {Math.round(r.carbs_g || 0)}g · F {Math.round(r.fat_g || 0)}g
                      </span>
                    )}
                    <span className="ff-chip">Serves {r.servings}</span>
                    {(r.tags || []).slice(0, 1).map((t) => (
                      <span key={t} className="ff-chip">{t}</span>
                    ))}
                  </div>
                  {r.missing_staples && r.missing_staples.length > 0 && !staplesDone.has(r.recipe_id) && (
                    <div
                      className="rounded-xl border border-[#FBE9C4] bg-[#FFF8E7] px-3 py-2.5"
                      data-testid={`missing-staples-${r.recipe_id}`}
                    >
                      <div className="text-[10px] uppercase tracking-wide text-[#7A5A11] font-semibold flex items-center gap-1">
                        <span>⚠️</span> Not in the box
                      </div>
                      <div className="mt-1 text-[11px] text-[#5C6B61] leading-snug">
                        {r.missing_staples.map((s) => s.name).join(" · ")}
                      </div>
                      <button
                        onClick={() => addStaples(r)}
                        disabled={staplesBusy === r.recipe_id}
                        data-testid={`add-staples-${r.recipe_id}`}
                        className="mt-2 text-[11px] font-medium rounded-full px-3 py-1.5 bg-[#7A5A11] text-white hover:bg-[#5F4409] transition disabled:opacity-60"
                      >
                        {staplesBusy === r.recipe_id ? "Adding…" : `🛒 Add ${r.missing_staples.length} staple${r.missing_staples.length === 1 ? "" : "s"} to shopping list`}
                      </button>
                    </div>
                  )}
                  <div className="mt-auto pt-3 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => toggleSelect(r)}
                      data-testid="select-recipe-btn"
                      className={`text-xs font-medium rounded-full px-3 py-2 transition ${
                        isSel
                          ? "bg-[#3B5A3E] text-white"
                          : "bg-[#F3F0EA] text-[#3B5A3E] hover:bg-[#E5E2DA]"
                      }`}
                    >
                      {isSel ? "Selected ✓" : "Add to list"}
                    </button>
                    <button
                      onClick={() => sendOneToList(r)}
                      disabled={sendingId === r.recipe_id}
                      data-testid={`send-to-list-${r.recipe_id}`}
                      title={`Send "${r.title}" straight to a new shopping list`}
                      className="text-xs font-medium rounded-full px-3 py-2 bg-[#3B5A3E] text-white hover:bg-[#2A4230] transition disabled:opacity-60"
                    >
                      {sendingId === r.recipe_id ? "Sending…" : "🛒 Send to the shopping list"}
                    </button>
                    <LogKcalButton
                      kcal={r.calories_per_serving}
                      name={r.title}
                      source="manual"
                      protein_g={r.protein_g}
                      carbs_g={r.carbs_g}
                      fat_g={r.fat_g}
                      testid={`log-recipe-${r.recipe_id}`}
                    />
                    {isPro && (
                      <button
                        onClick={() => setShareTarget({ id: r.recipe_id, title: r.title })}
                        data-testid={`share-recipe-${r.recipe_id}`}
                        className="text-xs font-medium rounded-full px-3 py-2 bg-[#E8F0E9] text-[#3B5A3E] hover:bg-[#cfe0d0] transition"
                        title="Send this recipe to a friend"
                      >
                        💚 Share
                      </button>
                    )}
                    <button
                      onClick={() => remove(r.recipe_id)}
                      className="ml-auto text-xs text-[#8B9690] hover:text-[#C36952]"
                    >
                      Remove
                    </button>
                  </div>
                  {r.shared_from_name && (
                    <div className="text-[10px] text-[#3B5A3E] font-medium" data-testid={`shared-from-${r.recipe_id}`}>
                      Shared by {r.shared_from_name}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
              </div>
            );
          })()}
        </>
      )}

      {/* Scaling modal */}
      {scalingOpen && (
        <div
          className="fixed inset-0 z-40 bg-[#2A332C]/40 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={() => setScalingOpen(false)}
          data-testid="scaling-modal"
        >
          <div
            className="ff-card w-full max-w-lg p-6 md:p-8 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ff-label">Final step</div>
            <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              How many servings of each?
            </h2>
            <p className="text-sm text-[#5C6B61] mt-1">We&apos;ll scale ingredient quantities to match.</p>

            <ul className="mt-5 space-y-3">
              {selectedRecipes.map((r) => (
                <li key={r.recipe_id} className="flex items-center gap-3 py-2 border-b border-[#E5E2DA] last:border-0">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.title}</div>
                    <div className="text-xs text-[#8B9690]">recipe makes {r.servings}</div>
                  </div>
                  <div className="flex items-center gap-2" data-testid={`servings-${r.recipe_id}`}>
                    <button
                      type="button"
                      onClick={() => setServings(sv => ({ ...sv, [r.recipe_id]: Math.max(1, (sv[r.recipe_id] || r.servings) - 1) }))}
                      className="w-8 h-8 rounded-full bg-[#F3F0EA] text-[#3B5A3E] hover:bg-[#E5E2DA]"
                    >–</button>
                    <span className="w-8 text-center font-medium tabular-nums">{servings[r.recipe_id] ?? r.servings}</span>
                    <button
                      type="button"
                      onClick={() => setServings(sv => ({ ...sv, [r.recipe_id]: Math.min(50, (sv[r.recipe_id] || r.servings) + 1) }))}
                      className="w-8 h-8 rounded-full bg-[#F3F0EA] text-[#3B5A3E] hover:bg-[#E5E2DA]"
                    >+</button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setScalingOpen(false)} className="ff-btn-ghost">Cancel</button>
              <button
                onClick={buildList}
                disabled={creatingList}
                data-testid="confirm-build-list-btn"
                className="ff-btn-primary disabled:opacity-60"
              >
                {creatingList ? "Building…" : "Build list"}
              </button>
            </div>
          </div>
        </div>
      )}
      {shareTarget && (
        <ShareRecipeModal
          recipeId={shareTarget.id}
          recipeTitle={shareTarget.title}
          onClose={() => setShareTarget(null)}
        />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="ff-card p-10 text-center" data-testid="recipes-empty">
      <img
        src="https://images.unsplash.com/photo-1490818387583-1baba5e638af?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
        alt="" className="w-40 h-40 object-cover rounded-3xl mx-auto mb-6"
      />
      <h3 className="text-2xl font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>No recipes yet</h3>
      <p className="text-[#5C6B61] mt-2 max-w-md mx-auto">
        Find a recipe you love online, paste the link above, and we&apos;ll take it from there.
      </p>
    </div>
  );
}

